import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useContent } from "../context/ContentContext";
import { PageHeader } from "../components/PageHeader";
import { Target, Scale, Landmark, Users, MapPin, Phone, Mail, Clock } from "lucide-react";

const GOV_IMG = "https://images.unsplash.com/photo-1768783034899-3d71781a6f74?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxpbmRvbmVzaWElMjBnb3Zlcm5tZW50JTIwYnVpbGRpbmd8ZW58MHx8fHwxNzgzNjk2NDczfDA&ixlib=rb-4.1.0&q=85";

const D = {
  sejarah: "Badan Penanggulangan Bencana Daerah (BPBD) Kabupaten Kayong Utara dibentuk sebagai amanat Undang-Undang Nomor 24 Tahun 2007 tentang Penanggulangan Bencana dan Peraturan Pemerintah Nomor 21 Tahun 2008 tentang Penyelenggaraan Penanggulangan Bencana.\n\nBPBD Kayong Utara bertugas menyelenggarakan penanggulangan bencana secara terencana, terpadu, dan menyeluruh — mencakup tahap prabencana, tanggap darurat, dan pascabencana di seluruh wilayah Kabupaten Kayong Utara, Kalimantan Barat.",
  visi: "Terwujudnya masyarakat Kayong Utara yang tangguh, siap, dan mampu menghadapi bencana.",
  misi: "Mengurangi risiko bencana melalui pencegahan dan kesiapsiagaan\nMenyelenggarakan tanggap darurat yang cepat dan tepat\nMemulihkan kondisi pasca bencana secara berkelanjutan",
  dasarHukum: "UU No. 24 Tahun 2007 tentang Penanggulangan Bencana\nPP No. 21 Tahun 2008 tentang Penyelenggaraan Penanggulangan Bencana\nPermendagri No. 46 Tahun 2008 tentang Pedoman Organisasi dan Tata Kerja BPBD\nPerka BNPB No. 3 Tahun 2008 tentang Pedoman Pembentukan BPBD\nPeraturan Daerah Kabupaten Kayong Utara tentang Pembentukan BPBD",
  tupoksi: "Menetapkan pedoman dan pengarahan penanggulangan bencana\nMenetapkan standarisasi dan kebutuhan penyelenggaraan penanggulangan bencana\nMenyusun, menetapkan, dan menginformasikan peta rawan bencana\nMenyusun dan menetapkan prosedur tetap penanganan bencana\nMelaporkan penyelenggaraan penanggulangan bencana kepada Kepala Daerah\nMengendalikan pengumpulan dan penyaluran bantuan",
  alamat: "Jl. Bhayangkara, Sukadana, Kabupaten Kayong Utara, Kalimantan Barat 78852",
  telepon: "(0534) 771-112",
  email: "bpbd@kayongutarakab.go.id",
  jam: "Senin–Jumat 07.30–16.00 WIB · Posko Darurat 24 Jam",
};

const lines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);

const defaultStruktur = [
  { position: "Kepala Pelaksana BPBD", name: "—" },
  { position: "Sekretaris", name: "—" },
  { position: "Kabid Pencegahan & Kesiapsiagaan", name: "—" },
  { position: "Kabid Kedaruratan & Logistik", name: "—" },
  { position: "Kabid Rehabilitasi & Rekonstruksi", name: "—" },
];

export default function Profil() {
  const { c } = useContent();
  const [officials, setOfficials] = useState([]);
  const [expandSejarah, setExpandSejarah] = useState(false);
  useEffect(() => {
    api.get("/officials").then((r) => setOfficials(r.data)).catch(() => {});
  }, []);
  const struktur = officials.length ? officials : defaultStruktur;

  return (
    <div data-testid="profil-page">
      <PageHeader title="Profil BPBD" subtitle="Mengenal Badan Penanggulangan Bencana Daerah Kabupaten Kayong Utara" />

      <section className="max-w-7xl mx-auto px-4 py-16 grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <p className="section-label text-orange-600 mb-2">Tentang Kami</p>
          <h2 className="font-heading text-lg font-bold text-slate-900 mb-4">Sejarah Pembentukan</h2>
          <div className={`text-slate-600 leading-relaxed text-sm md:text-base whitespace-pre-line text-reading ${expandSejarah ? "" : "line-clamp-4"}`} data-testid="profil-sejarah">
            {c("profil_sejarah", D.sejarah)}
          </div>
          <button
            onClick={() => setExpandSejarah(!expandSejarah)}
            className="mt-3 text-sm font-bold text-orange-600 hover:underline inline-flex items-center gap-1"
            data-testid="profil-sejarah-toggle"
          >
            {expandSejarah ? "Tutup ↑" : "Selengkapnya →"}
          </button>
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            <div className="hover-lift border border-slate-200 bg-white rounded-xl p-6" data-testid="visi-card">
              <Target className="text-orange-600 mb-3" size={24} />
              <h3 className="font-heading font-bold text-slate-900 mb-2">Visi</h3>
              <p className="text-sm text-slate-600">{c("profil_visi", D.visi)}</p>
            </div>
            <div className="hover-lift border border-slate-200 bg-white rounded-xl p-6" data-testid="misi-card">
              <Scale className="text-sky-600 mb-3" size={24} />
              <h3 className="font-heading font-bold text-slate-900 mb-2">Misi</h3>
              <ul className="text-sm text-slate-600 list-disc pl-4 space-y-1">
                {lines(c("profil_misi", D.misi)).map((m) => <li key={m}>{m}</li>)}
              </ul>
            </div>
          </div>
        </div>
        <img src={c("profil_foto", GOV_IMG)} alt="Kantor BPBD" className="rounded-xl border border-slate-200 w-full object-cover h-96" data-testid="profil-foto" />
      </section>

      <section className="bg-slate-100 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-16">
          <p className="section-label text-orange-600 mb-2">Landasan</p>
          <h2 className="font-heading text-lg font-bold text-slate-900 mb-8">Dasar Hukum & Tugas Pokok</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="hover-lift bg-white border border-slate-200 rounded-xl p-7" data-testid="dasar-hukum-card">
              <Landmark className="text-orange-600 mb-4" size={26} />
              <h3 className="font-heading font-bold text-slate-900 mb-3">Dasar Hukum</h3>
              <ul className="text-sm text-slate-600 space-y-2 list-disc pl-4">
                {lines(c("profil_dasar_hukum", D.dasarHukum)).map((x) => <li key={x}>{x}</li>)}
              </ul>
            </div>
            <div className="hover-lift bg-white border border-slate-200 rounded-xl p-7" data-testid="tupoksi-card">
              <Users className="text-sky-600 mb-4" size={26} />
              <h3 className="font-heading font-bold text-slate-900 mb-3">Tugas Pokok & Fungsi</h3>
              <ul className="text-sm text-slate-600 space-y-2 list-disc pl-4">
                {lines(c("profil_tupoksi", D.tupoksi)).map((x) => <li key={x}>{x}</li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 py-16">
        <p className="section-label text-orange-600 mb-2">Organisasi</p>
        <h2 className="font-heading text-lg font-bold text-slate-900 mb-10">Struktur Organisasi</h2>
        <div className="flex flex-col items-center" data-testid="struktur-bagan">
          {/* Pimpinan */}
          <div className="hover-lift w-full max-w-sm bg-slate-950 text-white rounded-xl p-6 text-center border-2 border-orange-600" data-testid="struktur-pimpinan">
            <div className="w-14 h-14 mx-auto rounded-full bg-orange-600 flex items-center justify-center font-heading font-bold text-lg mb-3">
              {(struktur[0]?.name || "—").split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase()}
            </div>
            <p className="font-heading font-bold text-lg">{struktur[0]?.name || "—"}</p>
            <p className="section-label text-orange-400 mt-1.5">{struktur[0]?.position}</p>
          </div>
          {struktur.length > 1 && (
            <>
              <div className="w-px h-10 bg-slate-300" />
              <div className="w-full grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {struktur.slice(1).map((s) => (
                  <div key={s.position} className="hover-lift bg-white border border-slate-200 rounded-xl p-5 text-center" data-testid={`struktur-card-${s.position}`}>
                    <div className="w-11 h-11 mx-auto rounded-full bg-orange-50 text-orange-600 flex items-center justify-center font-heading font-bold mb-3">
                      {(s.name || "—").split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase()}
                    </div>
                    <p className="font-heading font-bold text-slate-900 text-sm">{s.name || "—"}</p>
                    <p className="text-xs text-slate-500 mt-1.5">{s.position}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        {officials.length === 0 && <p className="text-xs text-slate-400 mt-5 text-center">Data pejabat akan diperbarui oleh administrator melalui menu Struktur Organisasi.</p>}
      </section>

      <section className="bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-4 py-16 grid lg:grid-cols-2 gap-10">
          <div>
            <p className="section-label text-orange-500 mb-2">Hubungi Kami</p>
            <h2 className="font-heading text-lg font-bold mb-8">Kontak & Lokasi</h2>
            <div className="space-y-5 text-sm" data-testid="profil-kontak">
              <div className="flex gap-3"><MapPin className="text-orange-500 shrink-0" size={18} /><p>{c("kontak_alamat", D.alamat)}</p></div>
              <div className="flex gap-3"><Phone className="text-orange-500 shrink-0" size={18} /><p>Darurat: <span className="font-mono font-bold text-orange-400">{c("kontak_darurat", "112")}</span> · Layanan: <span className="font-mono">{c("kontak_telepon", D.telepon)}</span></p></div>
              <div className="flex gap-3"><Mail className="text-orange-500 shrink-0" size={18} /><p className="font-mono">{c("kontak_email", D.email)}</p></div>
              <div className="flex gap-3"><Clock className="text-orange-500 shrink-0" size={18} /><p>{c("kontak_jam", D.jam)}</p></div>
            </div>
          </div>
          <iframe
            title="Peta Lokasi Kantor BPBD Kayong Utara"
            src="https://www.openstreetmap.org/export/embed.html?bbox=109.95%2C-1.30%2C110.15%2C-1.18&layer=mapnik&marker=-1.2454%2C110.0743"
            className="w-full h-72 rounded-xl border border-slate-800"
            data-testid="office-map"
          />
        </div>
      </section>
    </div>
  );
}
